import { useState, useEffect, useRef, useCallback } from "react";
import { getDevices, getClients, getAgents, updateDevice, deleteDevice, getMeshcentralNodes } from "../api";
import { Monitor, Wifi, WifiOff, RefreshCw, AlertTriangle, AlertOctagon, User, Trash2, Activity, Laptop, Server, Cpu, Smartphone } from "lucide-react";
import RemoteButton from "../components/RemoteButton";
import { tokens, sx, FullPageSpinner } from "../theme";
import useIsMobile from "../hooks/useIsMobile";

// Glass hexagon KPI icon — neon rim + translucent tinted-glass interior + glowing
// accent icon. clip-path can't take box-shadow, so the glow is a drop-shadow filter.
const HEX_CLIP = "polygon(25% 5%,75% 5%,100% 50%,75% 95%,25% 95%,0 50%)";
function KpiHex({ icon: Icon, color }) {
  return (
    <div style={{
      position:"relative", width:42, height:42, flexShrink:0, clipPath:HEX_CLIP,
      display:"flex", alignItems:"center", justifyContent:"center", background:color,
      filter:`drop-shadow(0 0 7px ${color}8c) drop-shadow(0 3px 5px rgba(0,0,0,0.3))`,
    }}>
      <div style={{
        position:"absolute", inset:1.5, clipPath:HEX_CLIP,
        background:`radial-gradient(circle at 34% 24%, rgba(255,255,255,0.16), transparent 60%), linear-gradient(157deg, ${color}4d, ${color}0f), #0a1326`,
      }}/>
      <Icon size={19} color={color} style={{ position:"relative", zIndex:2, filter:`drop-shadow(0 0 5px ${color}8c)` }}/>
    </div>
  );
}
const KPI_ICON  = { total: Monitor, online: Activity, warning: AlertTriangle, critical: AlertOctagon, offline: WifiOff };
const KPI_COLOR = { total: "#4f6fff", online: "#10b981", warning: "#f59e0b", critical: "#ef4444", offline: "#8d99ae" };

// Pick a fitting device glyph from the OS/model string.
function DeviceIcon({ os = "", size = 21, color = "#fff", style }) {
  const s = String(os).toLowerCase();
  const Icon = /android|ios|iphone|ipad/.test(s) ? Smartphone : /server/.test(s) ? Server : /book|laptop|surface/.test(s) ? Laptop : Monitor;
  return <Icon size={size} color={color} style={style} />;
}

// "Same user" links — when this device's contact also owns other devices
// (desktop + phone), link straight to each sibling's Device360 page.
// Owner key — a device belongs to a contact (customer) OR an agent (staff).
// Match siblings on whichever one links THIS device, so a staff member's phone
// + laptop cross-link just like a customer's do.
function ownerKey(d) {
  return d.contact_id ? `c:${d.contact_id}` : d.agent_id ? `a:${d.agent_id}` : null;
}

// Mobile devices belonging to the same user, shown nested INSIDE their computer
// card (directly under the computer's info) — a phone is attached to its owner's
// machine rather than floating as a separate card.
function NestedMobiles({ phones }) {
  if (!phones || !phones.length) return null;
  const online = (d) => d.status === "online" || (d.last_seen && Date.now() - new Date(d.last_seen).getTime() < 5 * 60_000);
  return (
    <div style={{ marginBottom:12, paddingTop:10, borderTop:"1px dashed var(--t-border-soft)" }}>
      <div style={{ fontSize:10, color:"var(--t-text-faint)", textTransform:"uppercase", letterSpacing:"0.04em", fontWeight:700, marginBottom:6 }}>
        Mobile devices
      </div>
      {phones.map(p => (
        <a key={p.id} href={`/devices/${encodeURIComponent(p.id)}`}
          onClick={e => e.stopPropagation()}
          title={`Open ${p.device_name || p.computer_name} in Device360`}
          style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 8px", marginBottom:4, borderRadius:8,
            textDecoration:"none", background:"rgba(79,111,255,0.07)", border:"1px solid rgba(79,111,255,0.18)" }}>
          <Smartphone size={14} color={online(p) ? "#22c55e" : "var(--t-text-faint)"} style={{ flexShrink:0 }} />
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:12, fontWeight:600, color:"var(--t-text-primary)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
              {p.device_name || p.computer_name}
            </div>
            <div style={{ fontSize:10, color:"var(--t-text-faint)" }}>
              {[p.os, p.os_version].filter(Boolean).join(" ")}{p.last_seen ? ` · ${timeSince(p.last_seen)}` : ""}
            </div>
          </div>
          <span style={{ fontSize:9, fontWeight:700, padding:"2px 7px", borderRadius:99,
            background: online(p) ? "rgba(34,197,94,0.12)" : "rgba(71,85,105,0.15)",
            color: online(p) ? "#22c55e" : "var(--t-text-faint)" }}>
            {online(p) ? "online" : "offline"}
          </span>
        </a>
      ))}
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────
function timeSince(dateStr) {
  if (!dateStr) return "Never";
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60)    return `${diff}s ago`;
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function getMetrics(d) {
  return {
    cpu:      d.cpu_percent       ?? d.cpu_usage   ?? null,
    ram:      d.ram_percent       ?? d.ram_usage   ?? null,
    disk:     d.disk_used_percent ?? d.disk_usage  ?? null,
    ramUsed:  d.ram_used_gb       ?? d.health_ram_used_gb  ?? null,
    ramTotal: d.ram_total_gb      ?? d.health_ram_total_gb ?? null,
    diskUsed:  d.disk_used_gb  ?? null,
    diskTotal: d.disk_total_gb ?? null,
  };
}

// ── MetricBar ─────────────────────────────────────────────────
const MetricBar = ({ value, label }) => {
  const color = value == null ? "#475569"
    : value >= 90 ? "#ef4444"
    : value >= 75 ? "#f59e0b"
    : "#10b981";
  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, marginBottom:3 }}>
        <span style={{ color:"var(--t-text-label)" }}>{label}</span>
        <span style={{ color, fontWeight:600 }}>{value != null ? `${Math.round(value)}%` : "—"}</span>
      </div>
      <div style={{ height:4, background:"var(--t-surface-mid)", borderRadius:2 }}>
        <div style={{ height:4, background:color, borderRadius:2, width:`${value || 0}%`, transition:"width 0.3s" }}/>
      </div>
    </div>
  );
};

// ── RMM agent status chip ─────────────────────────────────────
function RmmStatusChip({ device }) {
  const lastSeen = device.last_seen ? new Date(device.last_seen).getTime() : 0;
  const age = Date.now() - lastSeen;
  // Match the backend's online definition (status flips offline after 5 min of
  // silence; agents check in every ~60s) so this page and Inventory agree.
  const online  = device.status === "online" || (lastSeen && age < 5 * 60_000);
  const stale   = lastSeen && age < 48 * 3600_000;   // within 48h but not recent
  const color   = online ? "#22c55e" : stale ? "#f59e0b" : "#475569";
  const label   = online ? "Agent online" : stale ? "Agent stale" : lastSeen ? "Agent offline" : "No agent";
  return (
    <div style={{ display:"flex", alignItems:"center", gap:6, padding:"5px 8px", background:"var(--t-surface-deep)", border:"1px solid var(--t-border-mid)", borderRadius:8, marginBottom:10 }}>
      <Activity size={12} color={color}/>
      <span style={{ fontSize:11, color, fontWeight:600 }}>{label}</span>
      {device.last_seen && <span style={{ fontSize:10, color:"var(--t-text-deep)", marginLeft:"auto" }}>{timeSince(device.last_seen)}</span>}
      {device.agent_version && <span style={{ fontSize:10, color:"var(--t-text-deep)", fontFamily:"monospace" }}>v{device.agent_version}</span>}
    </div>
  );
}

// ── Status config ─────────────────────────────────────────────
const statusConfig = {
  healthy:  { color:"#10b981", bg:"#052e16", label:"Healthy"  },
  warning:  { color:"#f59e0b", bg:"#3f3006", label:"Warning"  },
  critical: { color:"#ef4444", bg:"#450a0a", label:"Critical" },
  offline:  { color:"var(--t-text-label)", bg:"#1f2937", label:"Offline"  },
  online:   { color:"#10b981", bg:"#052e16", label:"Online"   },
  unknown:  { color:"var(--t-text-label)", bg:"#1f2937", label:"Unknown"  },
};

// ── Process alert chip ────────────────────────────────────────
const AlertChip = ({ device }) => {
  if (!device.last_alert_process) return null;
  return (
    <div style={{ display:"flex", alignItems:"center", gap:6, background:"#1c0e00", border:"1px solid #92400e", borderRadius:6, padding:"5px 8px", marginBottom:10 }}>
      <AlertTriangle size={12} color="#fbbf24"/>
      <span style={{ fontSize:11, color:"var(--badge-amber)", flex:1 }}>
        <strong>{device.last_alert_process}</strong>
        {device.last_alert_cpu       ? ` · CPU ${Math.round(device.last_alert_cpu)}%` : ""}
        {device.last_alert_memory_mb ? ` · ${device.last_alert_memory_mb} MB`         : ""}
      </span>
      <span style={{ fontSize:10, color:"#92400e" }}>{timeSince(device.last_alert_at)}</span>
    </div>
  );
};

// ── Assignment row ────────────────────────────────────────────
function AssignmentRow({ device, clients = [], contacts, agents, meshNodes, onUpdate }) {
  const [saving, setSaving] = useState(false);

  const handleChange = async (field, value) => {
    setSaving(true);
    try {
      await updateDevice(device.id, { [field]: value || null });
      onUpdate();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  // Changing the client clears any contact that belonged to the old client,
  // so the End User dropdown never shows a stale name from a different company.
  const handleClientChange = async (value) => {
    setSaving(true);
    try {
      await updateDevice(device.id, { client_id: value || null, contact_id: null });
      onUpdate();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const selStyle = {
    background:"var(--t-surface-mid)", border:"1px solid var(--t-border-strong)", borderRadius:6,
    padding:"5px 8px", color:"var(--t-text-primary)", fontSize:11, cursor:"pointer",
    width:"100%", opacity: saving ? 0.6 : 1,
  };

  const clientContacts = contacts.filter(c => c.client_id === device.client_id);

  return (
    <div style={{ marginBottom:12 }}>
      <div style={{ fontSize:10, fontWeight:700, color:"var(--t-text-deep)", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:6, display:"flex", alignItems:"center", gap:4 }}>
        <User size={10}/> Assigned To
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
        {/* Client (company) */}
        <div>
          <div style={{ fontSize:10, color:"var(--t-text-label)", marginBottom:3 }}>Client</div>
          <select
            value={device.client_id || ""}
            onChange={e => handleClientChange(e.target.value)}
            style={{ ...selStyle, ...(device.client_id ? {} : { borderColor:"#92400e", color:"var(--badge-amber)" }) }}
            disabled={saving}
          >
            <option value="">Unassigned</option>
            {clients.map(c => (
              <option key={c.id} value={c.id}>{c.company_name}</option>
            ))}
          </select>
        </div>
        {/* End user (contact) */}
        <div>
          <div style={{ fontSize:10, color:"var(--t-text-label)", marginBottom:3 }}>End User</div>
          <select
            value={device.contact_id || ""}
            onChange={e => handleChange("contact_id", e.target.value)}
            style={selStyle}
            disabled={saving}
          >
            <option value="">Unassigned</option>
            {clientContacts.map(c => (
              <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>
            ))}
          </select>
        </div>
        {/* Staff member (agent) */}
        <div>
          <div style={{ fontSize:10, color:"var(--t-text-label)", marginBottom:3 }}>Staff Member</div>
          <select
            value={device.agent_id || ""}
            onChange={e => handleChange("agent_id", e.target.value)}
            style={selStyle}
            disabled={saving}
          >
            <option value="">Unassigned</option>
            {agents.map(a => (
              <option key={a.id} value={a.id}>{a.first_name} {a.last_name} ({a.role})</option>
            ))}
          </select>
        </div>
        {/* MeshCentral manual link — only shown when not yet linked */}
        {!device.meshcentral_node_id && (
          <div>
            <div style={{ fontSize:10, color:"var(--t-text-label)", marginBottom:3 }}>Link MeshCentral Node</div>
            <select
              value=""
              onChange={e => { if (e.target.value) handleChange("meshcentral_node_id", e.target.value); }}
              style={{ ...selStyle, color: "#94a3b8" }}
              disabled={saving}
            >
              <option value="">— select node to link —</option>
              {(meshNodes || []).map(n => (
                <option key={n.node_id} value={n.node_id}>
                  {n.name || n.node_id}{n.os ? ` · ${n.os.split(" - ")[0]}` : ""}
                </option>
              ))}
              {(meshNodes || []).length === 0 && (
                <option disabled>No unlinked nodes available</option>
              )}
            </select>
          </div>
        )}
        {/* Unlink option when already linked */}
        {device.meshcentral_node_id && (
          <div>
            <div style={{ fontSize:10, color:"var(--t-text-label)", marginBottom:3 }}>MeshCentral Node</div>
            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
              <span style={{ fontSize:10, color:"#22c55e", flex:1, fontFamily:"monospace", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                🔗 Linked
              </span>
              <button
                onClick={() => handleChange("meshcentral_node_id", null)}
                disabled={saving}
                style={{ fontSize:10, color:"#f87171", background:"none", border:"1px solid var(--t-border-strong)", borderRadius:4, padding:"2px 6px", cursor:"pointer" }}
              >
                Unlink
              </button>
            </div>
          </div>
        )}
        {/* RustDesk (Main) — auto-deployed + assigned by the agent, not manually linked */}
        <div>
          <div style={{ fontSize:10, color:"var(--t-text-label)", marginBottom:3 }}>RustDesk ID (Main)</div>
          <span style={{ fontSize:11, fontFamily:"monospace", color: device.rustdesk_id ? "#38bdf8" : "var(--t-text-faint)" }}>
            {device.rustdesk_id ? `🖥️ ${device.rustdesk_id}` : "— deploys automatically on agent check-in —"}
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Compact metric tile (matches the Clients-card stat aesthetic) ──────
function DStat({ label, value, sub }) {
  const v = value == null || isNaN(value) ? null : Math.round(value);
  const color = v == null ? "var(--t-text-faint)" : v >= 90 ? "#f87171" : v >= 75 ? "#fbbf24" : "#34d399";
  return (
    <div style={{ background:"var(--t-surface-deep)", border:"1px solid var(--t-border-soft)", borderRadius:9, padding:"9px 10px" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:5 }}>
        <span style={{ fontSize:9, fontWeight:700, color:"var(--t-text-faint)", textTransform:"uppercase", letterSpacing:"0.05em" }}>{label}</span>
        <span style={{ fontSize:13, fontWeight:700, color }}>{v == null ? "—" : `${v}%`}</span>
      </div>
      <div style={{ height:4, background:"rgba(148,163,184,0.12)", borderRadius:99, overflow:"hidden" }}>
        <div style={{ height:"100%", width:`${v || 0}%`, background:color, borderRadius:99, transition:"width 0.3s" }}/>
      </div>
      {sub && <div style={{ fontSize:9, color:"var(--t-text-deep)", marginTop:4, textAlign:"right" }}>{sub}</div>}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────
export default function Devices() {
  const [devices,       setDevices]       = useState([]);
  const [clients,       setClients]       = useState([]);
  const [agents,        setAgents]        = useState([]);
  const [contacts,      setContacts]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [search,        setSearch]        = useState("");
  const [filter,        setFilter]        = useState({ client_id:"", status:"" });
  const [lastRefresh,   setLastRefresh]   = useState(null);
  const [countdown,     setCountdown]     = useState(30);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting,      setDeleting]      = useState(null);
  const [meshNodes,     setMeshNodes]     = useState([]);
  const intervalRef   = useRef(null);
  const countdownRef  = useRef(null);
  const isMobile      = useIsMobile();

  const fetchDevices = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await getDevices({ client_id: filter.client_id });
      setDevices(Array.isArray(res.data) ? res.data : []);
      setLastRefresh(new Date());
      setCountdown(30);
    } catch (e) { console.error(e); }
    finally { if (!silent) setLoading(false); }
  }, [filter]);

  const handleDeleteDevice = async (id) => {
    setDeleting(id);
    try {
      await deleteDevice(id);
      setDevices(prev => prev.filter(d => d.id !== id));
      setConfirmDelete(null);
    } catch (e) { console.error(e); }
    finally { setDeleting(null); }
  };

  useEffect(() => { fetchDevices(); }, [fetchDevices]);

  useEffect(() => {
    Promise.all([getClients(), getAgents(), getMeshcentralNodes()]).then(([cl, ag, mn]) => {
      const clientList = Array.isArray(cl.data) ? cl.data : [];
      setClients(clientList);
      setAgents(Array.isArray(ag.data) ? ag.data : []);
      const allNodes = mn.data?.nodes || [];
      setMeshNodes(allNodes.filter(n => !n.matched));
      Promise.all(clientList.map(c =>
        fetch(`${process.env.REACT_APP_API_URL}/api/clients/${c.id}/contacts`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("midtc_token")}` }
        }).then(r => r.json()).then(d => Array.isArray(d) ? d.map(ct => ({ ...ct, client_id: c.id })) : [])
      )).then(results => setContacts(results.flat())).catch(() => {});
    }).catch(console.error);
  }, []);

  useEffect(() => {
    intervalRef.current  = setInterval(() => fetchDevices(true), 30000);
    countdownRef.current = setInterval(() => setCountdown(c => c > 0 ? c - 1 : 30), 1000);
    return () => { clearInterval(intervalRef.current); clearInterval(countdownRef.current); };
  }, [fetchDevices]);

  // Apply the active filter set first; summary stats reflect what's actually shown.
  const filtered = devices.filter(d => {
    if (filter.status && d.status !== filter.status) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = [
        d.device_name, d.computer_name, d.client_name, d.last_user, d.agent_name, d.contact_name,
        d.os, d.os_version, d.manufacturer, d.model, d.ip_address, d.hardware_id, d.serial_number,
      ];
      if (!hay.some(v => (v || "").toString().toLowerCase().includes(q))) return false;
    }
    return true;
  });

  // Status options + counts are derived from the data the server returned (already
  // narrowed by client_id), so the dropdown always offers exactly the statuses
  // present in this view. Sorted by frequency so common statuses come first.
  const statusCounts = devices.reduce((acc, d) => {
    const k = d.status || "unknown";
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
  const statusOptions = Object.entries(statusCounts).sort((a, b) => b[1] - a[1]).map(([k]) => k);

  const summary = {
    total:    filtered.length,
    online:   filtered.filter(d => d.status === "online").length,
    warning:  filtered.filter(d => d.status === "warning").length,
    critical: filtered.filter(d => d.status === "critical").length,
    offline:  filtered.filter(d => d.status === "offline").length,
  };

  // Active (online / seen in the last 5 min) devices always on top, then alerting
  // devices within each group, then most-recently-seen, then name.
  const isActive = (d) => d.status === "online" || (d.last_seen && Date.now() - new Date(d.last_seen).getTime() < 5 * 60_000);
  const sorted = [...filtered].sort((a, b) => {
    const aAct = isActive(a) ? 1 : 0, bAct = isActive(b) ? 1 : 0;
    if (bAct !== aAct) return bAct - aAct;
    const aAlert = a.last_alert_process ? 1 : 0, bAlert = b.last_alert_process ? 1 : 0;
    if (bAlert !== aAlert) return bAlert - aAlert;
    const aSeen = a.last_seen ? new Date(a.last_seen).getTime() : 0;
    const bSeen = b.last_seen ? new Date(b.last_seen).getTime() : 0;
    if (bSeen !== aSeen) return bSeen - aSeen;
    return (a.device_name || "").localeCompare(b.device_name || "");
  });

  // A user's phone shows nested inside their computer card (not as its own card).
  // Build owner→phones across the full filtered set, and drop phones from the
  // top-level cards when their owner has a computer to nest under.
  const isMobileType = (d) => String(d.device_type).toLowerCase() === "mobile" || /android|ios/i.test(d.os || "");
  const mobilesByOwner = {};
  for (const d of sorted) {
    if (!isMobileType(d)) continue;
    const k = ownerKey(d);
    if (!k) continue;
    (mobilesByOwner[k] = mobilesByOwner[k] || []).push(d);
  }
  const ownersWithComputer = new Set(sorted.filter(d => !isMobileType(d) && ownerKey(d)).map(d => ownerKey(d)));
  const cards = sorted.filter(d => !(isMobileType(d) && ownerKey(d) && ownersWithComputer.has(ownerKey(d))));

  const inputStyle = { ...sx.input, fontSize: 13, padding: "8px 12px" };

  return (
    <div style={{ padding: isMobile ? 14 : 24, maxWidth:1400 }}>

      {/* Page head */}
      <div style={{
        paddingBottom:14, borderBottom:`1px solid ${tokens.border.soft}`,
        marginBottom:18, display:"flex", alignItems:"flex-end", justifyContent:"space-between",
        flexWrap:"wrap", gap:12,
      }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:800, color:tokens.text.primary, letterSpacing:"-0.5px" }}>Device Health</h1>
          <p style={{ color:tokens.text.faint, fontSize:13, marginTop:3 }}>
            {summary.total} total devices
            {lastRefresh && ` · Updated ${lastRefresh.toLocaleTimeString()}`}
            {` · Refreshing in ${countdown}s`}
          </p>
        </div>
        <button onClick={() => { fetchDevices(); setCountdown(30); }}
          style={{ ...sx.buttonGhost, padding:"8px 14px" }}>
          <RefreshCw size={14}/> Refresh Now
        </button>
      </div>

      {/* Summary cards */}
      <div style={{ display:"grid", gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(5,1fr)", gap:10, marginBottom:18 }}>
        {Object.entries(summary).map(([key, val]) => {
          const accent = KPI_COLOR[key] || tokens.accent.indigo;
          const Icon   = KPI_ICON[key]  || Cpu;
          return (
            <div key={key} style={{
              background: tokens.surface.panel,
              border: `1px solid ${tokens.border.soft}`,
              borderRadius: tokens.radius.md,
              padding: "13px 15px",
              position: "relative", overflow: "hidden",
              boxShadow: tokens.shadow.panel,
              transition: "all 0.18s",
              display: "flex", alignItems: "center", gap: 13,
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.borderColor = "var(--t-border-mid)"; e.currentTarget.style.boxShadow = "var(--t-shadow-panel-hi)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.borderColor = tokens.border.soft; e.currentTarget.style.boxShadow = tokens.shadow.panel; }}>
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 1, background: tokens.shadow.cardSheen }} />
              <KpiHex icon={Icon} color={accent} />
              <div>
                <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: -1, lineHeight: 1, color: key === "total" ? tokens.text.primary : accent }}>{val}</div>
                <div style={{ fontSize: 10, color: tokens.text.faint, marginTop: 5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>{key}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap" }}>
        <input placeholder="🔍 Search device, client, user or agent..."
          value={search} onChange={e => setSearch(e.target.value)}
          style={{ ...inputStyle, flex: isMobile ? "1 1 100%" : 1, minWidth:200 }}/>
        <select value={filter.client_id} onChange={e => setFilter(p=>({...p,client_id:e.target.value}))} style={{ ...inputStyle, cursor:"pointer" }}>
          <option value="">All Customers</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
        </select>
        <select value={filter.status} onChange={e => setFilter(p=>({...p,status:e.target.value}))} style={{ ...inputStyle, cursor:"pointer" }}>
          <option value="">All Statuses ({devices.length})</option>
          {statusOptions.map(s => <option key={s} value={s}>{s} ({statusCounts[s]})</option>)}
        </select>
        {(filter.status || filter.client_id || search) && (
          <button
            onClick={() => { setFilter({ client_id:"", status:"" }); setSearch(""); }}
            style={{ ...inputStyle, cursor:"pointer", background: tokens.surface.panel, color: tokens.text.faint, border: `1px solid ${tokens.border.soft}` }}
            title="Clear all filters"
          >Clear</button>
        )}
      </div>

      {/* Device grid */}
      {loading ? (
        <FullPageSpinner />
      ) : sorted.length === 0 ? (
        <div style={{ textAlign:"center", padding:60, color:tokens.text.deep, fontSize:14 }}>
          {search ? `No devices found matching "${search}"` : "No devices found."}
        </div>
      ) : (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(330px,1fr))", gap:14 }}>
          {cards.map(d => {
            const cfg     = statusConfig[d.status] || statusConfig.unknown;
            const metrics = getMetrics(d);
            const name    = d.device_name || d.computer_name || "Unknown";
            return (
              <div key={d.id}
                onClick={(e) => {
                  if (e.target.closest('button, select, input, a')) return;
                  window.location.href = `/devices/${encodeURIComponent(d.id)}`;
                }}
                style={{
                  background: tokens.surface.panel,
                  border:`1px solid ${d.last_alert_process ? "#92400e" : tokens.border.soft}`,
                  borderRadius: tokens.radius.md,
                  padding: 16,
                  cursor: "pointer",
                  boxShadow: tokens.shadow.panel,
                  transition: "all 0.18s",
                }}
                onMouseOver={x => { x.currentTarget.style.transform = "translateY(-1px)"; x.currentTarget.style.borderColor = "rgba(99,102,241,0.25)"; }}
                onMouseOut={x => { x.currentTarget.style.transform = "translateY(0)"; x.currentTarget.style.borderColor = d.last_alert_process ? "#92400e" : tokens.border.soft; }}>
                {/* Header — icon tile + name + status pill (Clients-card style) */}
                <div style={{ display:"flex", gap:12, alignItems:"flex-start", marginBottom:13 }}>
                  <div style={{
                    width:44, height:44, borderRadius:12, flexShrink:0, position:"relative", overflow:"hidden",
                    background:"linear-gradient(135deg, rgba(79,111,255,0.18), rgba(123,97,255,0.18))",
                    border:"1px solid rgba(79,111,255,0.28)",
                    display:"flex", alignItems:"center", justifyContent:"center",
                    boxShadow:"0 0 18px rgba(79,111,255,0.18), inset 0 1px 0 rgba(255,255,255,0.12)",
                  }}>
                    <DeviceIcon os={d.os} size={21} color="#22d3ee" style={{ position:"relative", zIndex:1, filter:"drop-shadow(0 0 6px rgba(0,212,255,0.5))" }}/>
                    {/* status dot on the tile (offline-safe — solid colors only) */}
                    <span style={{ position:"absolute", top:5, right:5, width:7, height:7, borderRadius:"50%", background:cfg.color, boxShadow:`0 0 6px ${cfg.color}`, zIndex:2 }}/>
                    <div style={{ position:"absolute", inset:0, background:"radial-gradient(circle at 30% 25%, rgba(255,255,255,0.18), transparent 55%)", pointerEvents:"none" }}/>
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:3 }}>
                      <span style={{ fontWeight:700, fontSize:15, color:"var(--t-text-primary)", letterSpacing:"-0.2px", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", flex:1 }}>{name}</span>
                      <span style={{ display:"inline-flex", alignItems:"center", gap:5, fontSize:9, fontWeight:700, letterSpacing:"0.04em", textTransform:"uppercase", padding:"3px 8px", borderRadius:99, background:cfg.bg, color:cfg.color, flexShrink:0 }}>
                        <span style={{ width:6, height:6, borderRadius:"50%", background:cfg.color, boxShadow:`0 0 6px ${cfg.color}` }}/>{cfg.label}
                      </span>
                    </div>
                    <div style={{ fontSize:12, color:"var(--t-text-faint)", display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                      {d.client_name && <span style={{ color:"#f59e0b", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:150 }}>{d.client_name}</span>}
                      <span style={{ display:"inline-flex", alignItems:"center", gap:4 }}>{d.status==="offline" ? <WifiOff size={10}/> : <Wifi size={10}/>}{timeSince(d.last_seen)}</span>
                    </div>
                    {(d.os || d.last_user) && (
                      <div style={{ fontSize:11, color:"var(--t-text-deep)", marginTop:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {[d.os, d.last_user && `👤 ${d.last_user}`].filter(Boolean).join("  ·  ")}
                      </div>
                    )}
                  </div>
                </div>

                <AlertChip device={d}/>

                <RmmStatusChip device={d}/>

                <NestedMobiles phones={mobilesByOwner[ownerKey(d)] || []}/>

                {/* Stat tiles — CPU / RAM / Disk */}
                <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, marginBottom:12 }}>
                  <DStat label="CPU"  value={metrics.cpu}  />
                  <DStat label="RAM"  value={metrics.ram}  sub={metrics.ramUsed != null && metrics.ramTotal != null ? `${Number(metrics.ramUsed).toFixed(1)}/${Number(metrics.ramTotal).toFixed(0)}G` : null}/>
                  <DStat label="Disk" value={metrics.disk} sub={metrics.diskUsed != null && metrics.diskTotal != null ? `${Number(metrics.diskUsed).toFixed(0)}/${Number(metrics.diskTotal).toFixed(0)}G` : null}/>
                </div>

                {/* Assignment dropdowns */}
                <AssignmentRow
                  device={d}
                  clients={clients}
                  contacts={contacts}
                  agents={agents}
                  meshNodes={meshNodes}
                  onUpdate={fetchDevices}
                />

                {/* Actions */}
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                  <RemoteButton
                    mode="rustdesk"
                    deviceId={d.id}
                    deviceName={name}
                    contactName={d.agent_name || d.contact_name || d.client_name}
                    rustdeskId={d.rustdesk_id}
                    size="sm"
                    fullWidth
                  />
                  <RemoteButton
                    mode="mesh"
                    deviceId={d.id}
                    deviceName={name}
                    contactName={d.agent_name || d.contact_name || d.client_name}
                    meshcentralNodeId={d.meshcentral_node_id}
                    size="sm"
                    fullWidth
                  />
                  <button
                    style={{ gridColumn:"1 / -1", background:"rgba(79,111,255,0.08)", color:"var(--btn-remote-text, #cdd9ef)", fontSize:12, fontWeight:600, padding:"9px 10px", borderRadius:10, border:"1px solid rgba(79,111,255,0.25)", cursor:"pointer", transition:"all 0.2s ease", boxShadow:"inset 0 1px 0 rgba(255,255,255,0.05)" }}
                    onMouseEnter={e => { e.currentTarget.style.background="rgba(79,111,255,0.18)"; e.currentTarget.style.borderColor="rgba(79,111,255,0.6)"; e.currentTarget.style.boxShadow="0 0 18px rgba(79,111,255,0.25)"; e.currentTarget.style.color="#fff"; }}
                    onMouseLeave={e => { e.currentTarget.style.background="rgba(79,111,255,0.08)"; e.currentTarget.style.borderColor="rgba(79,111,255,0.25)"; e.currentTarget.style.boxShadow="inset 0 1px 0 rgba(255,255,255,0.05)"; e.currentTarget.style.color="var(--btn-remote-text, #cdd9ef)"; }}>
                    Full Report
                  </button>
                </div>

                {/* Remove device */}
                {confirmDelete === d.id ? (
                  <div style={{ marginTop:10, padding:"10px 12px", background:"#0f0a0a", border:"1px solid #7f1d1d", borderRadius:8, display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
                    <span style={{ fontSize:12, color:"#fca5a5" }}>Remove this device?</span>
                    <div style={{ display:"flex", gap:6 }}>
                      <button onClick={() => setConfirmDelete(null)} style={{ padding:"5px 10px", background:"var(--t-surface-mid)", border:"1px solid var(--t-border-strong)", borderRadius:6, color:"var(--t-text-muted)", fontSize:11, cursor:"pointer" }}>Cancel</button>
                      <button onClick={() => handleDeleteDevice(d.id)} disabled={deleting === d.id} style={{ padding:"5px 10px", background:"#b91c1c", border:"none", borderRadius:6, color:"#fff", fontSize:11, fontWeight:600, cursor:"pointer" }}>
                        {deleting === d.id ? "Removing..." : "Yes, Remove"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmDelete(d.id)}
                    style={{ marginTop:10, width:"100%", display:"flex", alignItems:"center", justifyContent:"center", gap:6, padding:"8px", background:"rgba(255,84,112,0.07)", border:"1px solid rgba(255,84,112,0.22)", borderRadius:10, color:"var(--btn-danger-text, #ff8a9b)", fontSize:12, fontWeight:600, cursor:"pointer", transition:"all 0.2s ease" }}
                    onMouseEnter={e => { e.currentTarget.style.background="rgba(255,84,112,0.15)"; e.currentTarget.style.borderColor="rgba(255,84,112,0.6)"; e.currentTarget.style.boxShadow="0 0 18px rgba(255,84,112,0.2)"; }}
                    onMouseLeave={e => { e.currentTarget.style.background="rgba(255,84,112,0.07)"; e.currentTarget.style.borderColor="rgba(255,84,112,0.22)"; e.currentTarget.style.boxShadow="none"; }}
                  >
                    <Trash2 size={12}/> Remove Device
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
